import { alpha, type Theme } from "@mui/material/styles";
import { Box, CircularProgress, Dialog, Typography } from "@mui/material";

interface LoadingDialogProps {
  readonly open: boolean;
  readonly label?: string;
  /** Optional second line under `label` (e.g. "This may take a few minutes, please keep this tab open."). */
  readonly description?: string;
  /** When false, the dialog does not block interaction (e.g. user can switch tabs). Default true. */
  readonly modal?: boolean;
  /**
   * When a parent MUI `Modal` is also open, both can share the default `zIndex.modal`.
   * Set to true so this `Dialog` renders on top and remains visible; use with {@link modal} (default) only.
   */
  readonly stackAboveMuiModal?: boolean;
}

const LoadingDialog = ({ open, label = "Loading…", description, modal = true, stackAboveMuiModal = false }: LoadingDialogProps) => {
  const isNonModal = !modal;

  const slotProps = isNonModal
    ? {
        root: {
          sx: {
            pointerEvents: "none" as const,
            backgroundColor: "transparent"
          }
        },
        paper: {
          sx: {
            px: 3,
            py: 2.5,
            borderRadius: 2,
            minWidth: 220,
            pointerEvents: "auto" as const
          }
        }
      }
    : {
        ...(stackAboveMuiModal
          ? {
              root: {
                sx: (theme: Theme) => ({ zIndex: theme.zIndex.modal + 1 })
              }
            }
          : {}),
        backdrop: {
          sx: (theme: { palette: { common: { black: string } } }) => ({
            backgroundColor: alpha(theme.palette.common.black, 0.15)
          })
        },
        paper: {
          sx: {
            px: 3,
            py: 2.5,
            borderRadius: 2,
            minWidth: 220
          }
        }
      };

  return (
    <Dialog
      open={open}
      hideBackdrop={isNonModal}
      disableEnforceFocus={isNonModal}
      disableAutoFocus={isNonModal}
      aria-busy={open}
      aria-live="polite"
      keepMounted
      maxWidth={false}
      slotProps={slotProps}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <CircularProgress size={28} thickness={3} />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" color="text.primary" component="div" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ textAlign: "center" }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Dialog>
  );
};

export default LoadingDialog;
